import { useEffect, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import type { SkillCard } from '../../lib/skills'

const RECENT_KEY = 'athena_recent_skills'
const RECENT_MAX = 3

type BusinessSkillsPanelProps = {
  skills: SkillCard[]
  isMobile: boolean
  onSelect: (displayMessage: string, skillId: string) => void
  onClose: () => void
  /** `popover` = floats above skills row; `float` = above chat input; `inline` = in flow */
  placement?: 'float' | 'inline' | 'popover'
}

const readRecent = (): string[] => {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : []
  } catch {
    return []
  }
}

const pushRecent = (skillId: string) => {
  const next = [skillId, ...readRecent().filter((id) => id !== skillId)].slice(0, RECENT_MAX)
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next))
  } catch {
    /* ignore quota */
  }
  return next
}

export const BusinessSkillsPanel = ({
  skills,
  isMobile,
  onSelect,
  onClose,
  placement = 'float',
}: BusinessSkillsPanelProps) => {
  const [query, setQuery] = useState('')
  const [recentIds, setRecentIds] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setRecentIds(readRecent())
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const q = query.trim().toLowerCase()
  const filtered = skills.filter((s) =>
    `${s.title} ${s.description}`.toLowerCase().includes(q)
  )
  const recentSkills = recentIds
    .map((id) => skills.find((s) => s.id === id))
    .filter((s): s is SkillCard => !!s)

  const select = (skill: SkillCard) => {
    setRecentIds(pushRecent(skill.id))
    onSelect(skill.title, skill.id)
  }

  const isSheet = isMobile && placement === 'float'

  const panel = (
    <div
      role="dialog"
      aria-label="All business skills"
      style={{
        maxWidth: isSheet ? undefined : '100%',
        margin: 0,
        maxHeight:
          placement === 'popover'
            ? 'min(520px, 58vh)'
            : placement === 'inline'
              ? 480
              : isSheet
                ? '80vh'
                : 'calc(100vh - 150px)',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--white)',
        border: isSheet ? 'none' : '1px solid var(--bd)',
        borderRadius: isSheet ? '16px 16px 0 0' : 14,
        boxShadow: isSheet
          ? '0 -8px 32px rgba(0,0,0,0.12)'
          : '0 14px 44px rgba(27,42,74,0.18)',
        overflow: 'hidden',
        animation: 'panelIn .24s cubic-bezier(.16,1,.3,1) both',
        width: '100%',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          padding: '15px 16px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
        }}
      >
        <span
          style={{
            fontFamily: "'Playfair Display',serif",
            fontSize: 14.5,
            color: 'var(--navy)',
          }}
        >
          All business skills
        </span>
        <button
          type="button"
          onClick={onClose}
          style={{
            fontSize: 11.5,
            color: 'var(--blue)',
            cursor: 'pointer',
            fontWeight: 500,
            background: 'none',
            border: 'none',
            padding: 0,
          }}
        >
          Close
        </button>
      </div>

      <div style={{ flexShrink: 0, padding: '0 16px 12px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'var(--bg)',
            border: '1px solid var(--bd)',
            borderRadius: 10,
            padding: '8px 11px',
          }}
        >
          <Search size={13} color="var(--muted2)" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search skills…"
            style={{
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 12.5,
              color: 'var(--text)',
              width: '100%',
            }}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              style={{
                border: 'none',
                background: 'none',
                padding: 0,
                cursor: 'pointer',
                color: 'var(--muted2)',
                display: 'flex',
              }}
            >
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      <div
        className="thin-sb"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '0 16px 16px',
        }}
      >
        {recentSkills.length > 0 && !q && (
          <div style={{ marginBottom: 14 }}>
            <div
              style={{
                fontSize: 9.5,
                fontWeight: 500,
                letterSpacing: '1.5px',
                textTransform: 'uppercase',
                color: 'var(--muted2)',
                marginBottom: 7,
              }}
            >
              Recently used
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {recentSkills.map((skill) => (
                <button
                  key={skill.id}
                  type="button"
                  onClick={() => select(skill)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    background: 'var(--bg)',
                    border: '1px solid var(--bd)',
                    borderRadius: 100,
                    padding: '5px 13px',
                    fontSize: 11.5,
                    color: 'var(--text)',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {skill.title}
                </button>
              ))}
            </div>
          </div>
        )}

        {filtered.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
              alignItems: 'stretch',
            }}
          >
            {filtered.map((skill) => (
              <SkillPickRow key={skill.id} skill={skill} onClick={() => select(skill)} />
            ))}
          </div>
        ) : (
          <div
            style={{
              textAlign: 'center',
              padding: '18px 0',
              fontSize: 12.5,
              color: 'var(--muted2)',
            }}
          >
            No skill by that name. Type your question below and Athena will still help.
          </div>
        )}
      </div>
    </div>
  )

  if (placement === 'inline') {
    return panel
  }

  if (placement === 'popover') {
    return (
      <div
        style={{
          position: 'absolute',
          bottom: '100%',
          left: 0,
          right: 0,
          marginBottom: 10,
          zIndex: 20,
        }}
      >
        {panel}
      </div>
    )
  }

  if (isMobile) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 600,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
      >
        <div
          onClick={onClose}
          style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,.35)' }}
        />
        <div style={{ position: 'relative', zIndex: 1 }}>{panel}</div>
      </div>
    )
  }

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '100%',
        left: 0,
        right: 0,
        padding: '0 22px 10px',
        zIndex: 490,
      }}
    >
      {panel}
    </div>
  )
}

const SkillPickRow = ({ skill, onClick }: { skill: SkillCard; onClick: () => void }) => {
  const [hov, setHov] = useState(false)
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        height: '100%',
        minHeight: 88,
        boxSizing: 'border-box',
        background: hov ? 'var(--bg)' : 'var(--white)',
        border: `1px solid ${hov ? 'var(--blue-bd)' : 'var(--bd)'}`,
        borderRadius: 12,
        padding: '12px 13px',
        cursor: 'pointer',
        transition: 'all .18s',
        textAlign: 'left',
        width: '100%',
      }}
    >
      <div
        style={{
          fontSize: 12.5,
          fontWeight: 500,
          color: 'var(--navy)',
          marginBottom: 4,
          lineHeight: 1.35,
          letterSpacing: 0,
        }}
      >
        {skill.title}
      </div>
      <div
        style={{
          fontSize: 11.5,
          color: 'var(--muted)',
          lineHeight: 1.5,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          flex: 1,
        }}
      >
        {skill.description}
      </div>
    </button>
  )
}
